import React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@app/shared";
import { Pages } from "../../../..";
import { Icons } from "../../../../shared";

type EndpointAndEventsProps = {
    facilityId: string;
    facilityName?: string;
    resetWizard: () => Promise<void>;
    className?: string;
};

export const FinishStep: React.FC<EndpointAndEventsProps> = ({ facilityId, facilityName, resetWizard, className }) => {
    const navigate = useNavigate();

    const handleGoToDevices = async () => {
        await resetWizard();
        navigate(Pages.Devices.getUrl());
    };

    const handleGoToEvents = async () => {
        await resetWizard();
        navigate(Pages.ClientEndpoints.getUrl());
    };

    return (
        <div className={`max-w-4xl mx-auto ${className || ""}`}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="px-6 py-8 text-center">
                    <div className="mb-6">
                        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <Icons.Success className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Device Setup Complete!</h2>
                        <p className="text-lg text-gray-600">Congratulations! You have successfully set up your device.</p>
                    </div>
                </div>
                <div className="bg-green-50 border-t border-green-200 px-6 py-6">
                    <h3 className="font-medium text-green-900 mb-4 text-center">Setup Summary</h3>
                    <div className="text-sm text-green-800 space-y-3 max-w-2xl mx-auto">
                        <div className="flex items-center gap-3">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>
                                Location set to <strong>{facilityName || facilityId}</strong>
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Device positioned on location map</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Detection shapes configured</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Device parameters configured and applied</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-green-600 font-bold">✓</span>
                            <span>Device rebooted and ready for operation</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="px-6 py-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">What would you like to do next?</h3>
                    <p className="text-gray-600 text-center mb-6">Your device is now fully operational. You can add events and endpoints to track specific activities, or simply go to your devices dashboard.</p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-sm transition-all">
                            <div className="text-center">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icons.Target className="w-6 h-6 text-blue-600" />
                                </div>
                                <h4 className="font-medium text-gray-900 mb-2">Configure Events & Endpoints</h4>
                                <p className="text-sm text-gray-600 mb-4">Set up specific events and endpoints that your device should track and monitor.</p>
                                <Button onClick={handleGoToEvents} className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                                    Add Events & Endpoints
                                </Button>
                            </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-6 hover:border-green-300 hover:shadow-sm transition-all">
                            <div className="text-center">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icons.MapMarker className="w-6 h-6 text-green-600" />
                                </div>
                                <h4 className="font-medium text-gray-900 mb-2">Go to Devices Dashboard</h4>
                                <p className="text-sm text-gray-600 mb-4">View all your devices, monitor their status, and manage your entire device network.</p>
                                <Button onClick={handleGoToDevices} className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                                    Go to Dashboard
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
